import React from 'react';
import css from 'styled-jsx/css';
import classnames from 'classnames';
import Link from 'next/link';
import { colors, dimensions } from '../common/Theme';

import Card from '../common/layout/Card';
import Button from '../common/elements/Button.js';

import { ReactComponent as FacebookLogo } from '../common/images/facebook-logo.svg';
import { ReactComponent as InstaLogo } from '../common/images/instagram-logo.svg';

import { FACEBOOK_LINK, INSTA_LINK } from '../../services/Configs';

const overrideStyles = css.resolve`
  .community__card.community__card {
    background: #ffffff;
    box-shadow: 0px 0px 34px 0 rgba(210, 210, 210, 0.5);
    padding: 0 20px 30px;
    margin: 0;
  }
  @media (min-width: 960px) {
    .community__card.community__card {
      padding: 0;
    }
  }
  .button {
    align-self: center;
    min-width: 200px;
    margin-bottom: 24px;
  }
`;

const keyPointsStyles = css`
  .key-point-text {
    margin: 0 0 8px;
    color: ${colors.ywgNavyLight};
  }
  .item {
    display: flex;
    flex-direction: column;
    margin: 0px auto 30px;
    padding: 0px;
    flex-basis: 100%;
    flex: 1;
  }
  .community__logo {
    margin: 0 auto;
    padding: 20px;
    width: 80px;
    height: 80px;
    background-color: #f2f4f6;
    border-radius: 50%;
    box-sizing: border-box;
  }
  .community__logo-image {
    display: block;
    margin: 0 auto;
    width: auto;
    height: 40px;
  }
  .card-subtitle {
    position: relative;
    margin: 10px 0 11px;
    padding: 19px 0 18px;
    color: ${colors.marine};
    font-size: 1.125rem;
    font-weight: bold;
    text-align: center;
  }
  .card-subtitle::before {
    position: absolute;
    display: block;
    top: 0;
    height: 100%;
    width: 100%;
    content: '';
    background-image: url(/static/images/ic-1.svg);
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
  .card-subtitle--2::before {
    background-image: url(/static/images/ic-2.svg);
  }
  .card-subtitle--3::before {
    background-image: url(/static/images/ic-3.svg);
  }
  .card-subtitle::after {
    position: absolute;
    display: inline-block;
    left: 50%;
    bottom: 9px;
    width: 37px;
    height: 4px;
    content: '';
    background-color: ${colors.ywgPinkishOrange};
    border-radius: 5px;
    transform: translateX(-40%);
  }
  @media (min-width: 960px) {
    .item {
      margin: 0px 60px 50px 60px;
    }
    .community__logo {
      padding: 45px;
      width: 155px;
      height: 155px;
    }
    .community__logo-image {
      height: 65px;
    }
    .card-subtitle {
      margin: 25px 0 10px;
      padding: 45px 0 35px;
      font-size: 1.25rem;
      text-align: left;
    }
    .card-subtitle::before {
      background-position: left;
    }
    .card-subtitle::after {
      bottom: 15px;
      left: 0;
      width: 56px;
      transform: none;
    }
  }
`;

const styles = css`
  .community__background {
    padding: 0 20px;
    margin: -40px auto auto;
    max-width: ${dimensions.ywgSectionMaxWidth};
  }
  @media (min-width: 960px) {
    .community__background {
      margin: -100px auto auto;
      position: relative;
    }
    .community__background:after {
      background: url('/static/images/plane-2.svg') no-repeat;
      background-position: center bottom;
      content: '';
      display: block;
      left: 0;
      width: 100%;
      height: 208px;
      position: absolute;
      bottom: -140px;
      z-index: -1;
    }
  }
  strong {
    font-weight: bolder;
  }
  .card-title {
    margin: 50px 0 30px;
    color: ${colors.ywgNavy};
    font-size: 1.5rem;
    text-align: center;
  }
  .join-tribe-title {
    position: relative;
    margin: 0 0 20px;
    padding: 20px 0 0;
    color: ${colors.marine};
    font-size: 1.125rem;
    font-weight: 500;
    text-align: center;
    opacity: 0.7;
  }
  .join-tribe-title::before {
    position: absolute;
    top: 0;
    left: 5%;
    width: 90%;
    height: 1px;
    content: '';
    background-color: ${colors.ywgVeryLightBlue};
  }
  .items {
    display: flex;
    flex-direction: column;
  }

  .social {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-self: center;
  }
  .social a {
    margin: 0px 15px;
    padding-bottom: 30px;
  }
  @media (min-width: 960px) {
    .card-title {
      margin: 60px 0 50px;
      font-size: 2.5rem;
    }
    .join-tribe-title {
      margin: 0 0 20px;
      padding-top: 30px;
      font-size: 1.25rem;
    }
    .items {
      flex-direction: row;
    }
  }
`;

const KeyPoints = () => {
  const cardsContentItems = [
    {
      title: (
        <>
          Une assurance <br /> adaptée
        </>
      ),
      image: '/static/images/ic-assurance-in.svg',
      text:
        'Le tarif de l’assurance YUPWEGO est calculé en fonction des zones traversées et des garanties nécessaires selon les pays, lors d’un tour du monde d’un an et demi par exemple.'
    },
    {
      title: 'Une communauté bienveillante',
      image: '/static/images/ic-communaute-in.svg',
      text: (
        <>
          La communauté gratuite YUPWEGO permet{' '}
          <strong>le partage d’expériences</strong>, d’avoir des{' '}
          <strong>réponses à ses questions</strong>,{' '}
          <strong>des contacts locaux</strong> qui peuvent interagir directement
          avec vous...
        </>
      )
    },
    {
      title: (
        <>
          Un voyage en <br /> toute sérénité
        </>
      ),
      image: '/static/images/ic-voyage-in.svg',
      text:
        'Avec une communauté grandissante dans votre poche et une assurance qui vous accompagne dans vos déplacements, vous n’avez qu’à profiter.'
    }
  ];

  return (
    <>
      {cardsContentItems.map((item, i) => (
        <div className="item" key={`community-functions-${i}`}>
          <span className="community__logo">
            <img src={item.image} alt="" className="community__logo-image" />
          </span>
          <h3 className={classnames('card-subtitle', `card-subtitle--${++i}`)}>
            {item.title}
          </h3>
          <p className="key-point-text">{item.text}</p>
        </div>
      ))}

      <style jsx>{keyPointsStyles}</style>
    </>
  );
};

const Community = () => {
  return (
    <section
      id="concept"
      className={classnames(overrideStyles.className, 'community__background')}
    >
      <Card
        className={classnames(overrideStyles.className, 'community__card')}
        squared
      >
        <h2 className="card-title">1, 2, 3 partez !</h2>
        <div className="items">
          <KeyPoints />
        </div>
        <Link href="/assurance">
          <Button
            shadowed
            className={classnames(overrideStyles.className)}
            href="/assurance"
          >
            Découvrez notre assurance
          </Button>
        </Link>
        <h3 className="join-tribe-title">Rejoignez la communauté</h3>
        <div className="social">
          <a href={FACEBOOK_LINK}>
            <FacebookLogo aria-hidden="true" />
            <span className="sr-only">YUPWEGO sur Facebook</span>
          </a>
          <a href={INSTA_LINK}>
            <InstaLogo aria-hidden="true" />
            <span className="sr-only">YUPWEGO sur Instagram</span>
          </a>
        </div>
      </Card>
      <style jsx>{styles}</style>
      {overrideStyles.styles}
    </section>
  );
};

export default Community;
